import React from 'react';
import Modal from 'react-modal';
import clone from 'lodash/clone';
import EditReportSave from './Popup/EditReportSave';
import PlanNotStarted from '../CommonComponents/PlanNotStarted';
import MissingInfo from '../ErrorPages/MissingInfoView';
import DragSortableList from '../CommonComponents/DragSortable';
import { isSegregatedClientAvailable } from '../../js/app/react/segregatedClient';
import {
  computeDataColumnReactProperties,
  computeYearColumnReactProperties,
  setResizeListener,
  setPageVScrollListener,
  getAdvancedReportClassProperty,
  getSpanValueReactProperties,
  getTableClassProperties,
  getTableStyleProperties,
  getColumnStyleProperties,
} from './AdvancedReportsHelpers';
import { PrimaryButton, DangerButton } from '../CommonComponents/Button';
import { EXTERNAL_HOST } from '../ConsumerArea/ConsumerAreaConstants';
import { ColumnsPopover } from './components/ColumnsPopover/ColumnsPopover';
import { RawButton } from '../CommonComponents/OPL/Button/Button.styles';
import { Icon } from '../CommonComponents/OPL/Icon/Icon';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { Select } from '../CommonComponents/OPL/Select/Select/Select';

class AdvancedReportsView extends React.Component {
  constructor(props) {
    super(props);
    const { initializeSection, clientId } = this.props;

    initializeSection(clientId, ADV.util.env('isAdviserArea'));

    this.state = {
      dataHeaderState: {
        className: 'header',
      },
      yearHeaderState: {
        className: 'header',
      },
      columnsHeader: [],
      columnsTable: [],
      isColumnsPopoverOpen: false,
    };

    this.setYearHeaderRef = this.setYearHeaderRef.bind(this);
    this.setDataHeaderRef = this.setDataHeaderRef.bind(this);
    this.onSort = this.onSort.bind(this);

    this.addListeners = this.addListeners.bind(this);
    this.removeListeners = this.removeListeners.bind(this);

    this.dispatchDomEvents = this.dispatchDomEvents.bind(this);

    this.updateDataHeaderStatePropertiesIfNecessary = this.updateDataHeaderStatePropertiesIfNecessary.bind(
      this,
    );

    this.updateDataAndYearHeaderStatePropertiesIfNecessary = this.updateDataAndYearHeaderStatePropertiesIfNecessary.bind(
      this,
    );

    this._yearHeaderRef = null;
    this._dataHeaderRef = null;
    this._refCount = 0;
  }

  UNSAFE_componentWillMount() {
    const { requestData, clientId, isPrint, printSelectedReport, printDone, fpId } = this.props;
    requestData(clientId, isPrint, printSelectedReport, fpId, printDone);
  }

  componentDidMount() {
    const { portalName } = this.props;

    document.title = `Reports - Advanced Reports | ${portalName}`;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { requestData, clientId, isPrint, printSelectedReport, printDone, fpId } = nextProps;
    if (fpId !== this.props.fpId) {
      requestData(clientId, isPrint, printSelectedReport, fpId, printDone);
    }
    if (nextProps.report.reportColumns) {
      const numberOfColumns = nextProps.report.reportColumns.length;
      this.setState({
        columnsHeader: nextProps.report.reportColumns.map((column, i) => ({
          content: (
            <li {...getColumnStyleProperties(isPrint, numberOfColumns, this.state.dataHeaderState)}>
              <span {...getSpanValueReactProperties(i, numberOfColumns)}>{column}</span>
            </li>
          ),
          name: column,
        })),
        columnsTable: nextProps.report.tableColumns.map((column, i) => (
          <li key={i} {...getColumnStyleProperties(isPrint, numberOfColumns)}>
            <ul className="data-column">
              {column.map((value, j) => (
                <li key={j}>
                  <span
                    {...getSpanValueReactProperties(
                      i,
                      numberOfColumns,
                      Math.round(value.Raw) < 0 ? ['red'] : null,
                    )}
                  >
                    {value.Formatted}
                  </span>
                </li>
              ))}
            </ul>
          </li>
        )),
      });
    }
  }

  updateDataHeaderStatePropertiesIfNecessary() {
    const isExternal = this.props.externalHost === EXTERNAL_HOST.FTCI;

    const dataHeaderState = computeDataColumnReactProperties(
      this._dataHeaderRef,
      this.props.isAdviserArea,
      isExternal,
    );
    if (!ADV.util.deepEqual(dataHeaderState, this.state.dataHeaderState)) {
      this.setState({
        ...this.state,
        dataHeaderState,
      });
    }
  }

  updateDataAndYearHeaderStatePropertiesIfNecessary() {
    const isExternal = this.props.externalHost === EXTERNAL_HOST.FTCI;

    const dataHeaderState = computeDataColumnReactProperties(
      this._dataHeaderRef,
      this.props.isAdviserArea,
      isExternal,
    );
    let shouldUpdate = false;
    if (!ADV.util.deepEqual(dataHeaderState, this.state.dataHeaderState)) {
      shouldUpdate = true;
    }

    const yearHeaderState = computeYearColumnReactProperties(
      this._yearHeaderRef,
      this.props.isAdviserArea,
      isExternal,
    );

    if (!ADV.util.deepEqual(yearHeaderState, this.state.yearHeaderState)) {
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      this.setState({
        ...this.state,
        dataHeaderState,
        yearHeaderState,
      });
    }
  }

  addListeners() {
    setResizeListener(this.updateDataHeaderStatePropertiesIfNecessary);
    setPageVScrollListener(this.updateDataAndYearHeaderStatePropertiesIfNecessary);
  }

  removeListeners() {
    setPageVScrollListener(null);
    setResizeListener(null);
  }

  dispatchDomEvents(domElement) {
    if (domElement) {
      this._refCount++;
    } else {
      this._refCount--;
    }

    switch (this._refCount) {
      case 0:
        this.removeListeners();
        break;
      case 2:
        this.addListeners();
        this.updateDataAndYearHeaderStatePropertiesIfNecessary();
        break;
    }
  }

  onSort(sortedList, sortedItem, originalRank, evt) {
    const columnsTable = clone(this.state.columnsTable, true);
    const columnsHeader = sortedList;
    const { setColumns, clientId, fpId } = this.props;
    const oldRank = parseInt(originalRank, 10);
    const newRank = parseInt(sortedItem.rank, 10);
    columnsTable.splice(newRank, 0, columnsTable.splice(oldRank, 1)[0]);

    setColumns(
      sortedList.map(x => x.name),
      clientId,
      fpId,
    );
    this.setState({ columnsHeader, columnsTable });
  }

  setYearHeaderRef(domElement) {
    this._yearHeaderRef = domElement;
    this.dispatchDomEvents(domElement);
  }

  setDataHeaderRef(domElement) {
    this._dataHeaderRef = domElement;
    this.dispatchDomEvents(domElement);
  }

  render() {
    const {
      clientId,
      fpId,
      setColumns,
      changeSelectedReportStartYear,
      setSelectedReport,
      errors,
      errorType,
      data,
      onChangeName,
      onChangeFirmLevel,
      onCloseErrors,
      selectedReport,
      saving,
      put,
      openModal,
      isPopupOpen,
      report,
      dismiss,
      deleteReport,
      isPrint,
      printSelectedReport,
      isSaveEnabled,
      fetchSelectedReport,
    } = this.props;
    const { columnsHeader, columnsTable, isColumnsPopoverOpen } = this.state;
    const isClientAvailable = isSegregatedClientAvailable(this.props, clientId);

    if (isClientAvailable) {
      switch (errorType) {
        case 'PlanNotStarted':
          return <PlanNotStarted clientId={clientId} />;
        case 'MissingInfo':
          return <MissingInfo clientId={clientId} errors={errors} />;
      }
    }

    if (!isClientAvailable || !data || !selectedReport) {
      return null;
    }

    const { originalReport, tableColumns, numberOfColumns, numberOfRows } = report;

    const reportOptions = [
      {
        label: 'Default Reports',
        options: Object.keys(data.DefaultReports).map(rep => ({
          label: rep,
          value: rep,
          isFirmLevelReport: false,
        })),
      },
      {
        label: 'Firm-Level Reports',
        options: Object.keys(data.FirmLevelReports || {}).map(rep => ({
          label: rep,
          value: rep,
          isFirmLevelReport: true,
        })),
      },
      {
        label: 'Saved Reports',
        options: Object.keys(data.SavedReports).map(rep => ({
          label: rep,
          value: rep,
          isFirmLevelReport: false,
        })),
      },
    ].filter(group => group.options.length > 0);

    return (
      <div
        id="AdvancedReports"
        className={getAdvancedReportClassProperty(numberOfColumns, isPrint)}
      >
        {isPrint ? (
          <header>
            <h2>
              Report:
              {printSelectedReport}
            </h2>
          </header>
        ) : null}
        {!isPrint ? (
          <>
            <header>
              <Text as="h1" fontSize="h3" fontWeight={600} mb="16">
                Reports
              </Text>
            </header>
            <Modal
              contentLabel=""
              isOpen={isPopupOpen}
              shouldCloseOnOverlayClick={false}
              className="EditReportWidget popup"
              overlayClassName="closerdiv"
            >
              <EditReportSave
                selectedReport={selectedReport}
                onChange={(field, value) => onChangeName(value, clientId)}
                onChangeFirmLevel={isFirmLevelReport =>
                  onChangeFirmLevel(isFirmLevelReport, clientId)
                }
                saving={saving}
                put={() => put(selectedReport, clientId, fpId)}
                dismiss={() => dismiss(clientId)}
                errors={errors}
                onCloseErrors={() => onCloseErrors(clientId)}
              />
            </Modal>
            <form className="adv-form">
              <ul className="horizontal-layout">
                <li>
                  <Select
                    className="md"
                    css="width: 270px"
                    onChange={v => {
                      const reportName = v ? v.value.toString() : '';
                      setSelectedReport(reportName, clientId, fpId);
                    }}
                    value={{
                      label: selectedReport.name,
                      value: selectedReport.name,
                    }}
                    aria-label="adv-report-type"
                    options={reportOptions}
                  />
                </li>

                {isSaveEnabled && this.props.isAdviserArea && (
                  <li>
                    <PrimaryButton
                      onClick={e => {
                        e.preventDefault();
                        openModal(clientId);
                        return false;
                      }}
                    >
                      Save Report
                    </PrimaryButton>
                  </li>
                )}
                {selectedReport.isDeletable &&
                  (selectedReport.type === 'SavedReports' ||
                    selectedReport.type === 'FirmLevelReports') &&
                  this.props.isAdviserArea && (
                    <li>
                      <DangerButton
                        onClick={e => {
                          e.preventDefault();
                          deleteReport(
                            selectedReport.name,
                            clientId,
                            fpId,
                            selectedReport.isFirmLevelReport,
                          );
                          return false;
                        }}
                      >
                        Delete Report
                      </DangerButton>
                    </li>
                  )}

                {data.StartYearOptions.length > 1 && (
                  <li className="align-right secondary">
                    <Select
                      className="md"
                      aria-label="adv-start-range"
                      css="width: 270px"
                      onChange={v => {
                        v = v ? v.value : 0;
                        changeSelectedReportStartYear(
                          v,
                          columnsHeader.map(x => x.name),
                          clientId,
                          fpId,
                        );
                      }}
                      value={{
                        label: data.StartYearOptions.find(x => x.value === selectedReport.startYear)
                          .label,
                        value: selectedReport.startYear,
                      }}
                      options={data.StartYearOptions}
                    />
                  </li>
                )}

                <li className="align-right">
                  <ColumnsPopover
                    isOpen={isColumnsPopoverOpen}
                    onOpenChange={isOpen => this.setState({ isColumnsPopoverOpen: isOpen })}
                    defaultColumns={columnsHeader.map(column => column.name)}
                    columnGroups={(() => {
                      const groups = {};

                      data.Columns.forEach(column => {
                        const group = data.ColumnsGroups[column];

                        if (!groups[group]) {
                          groups[group] = [];
                        }

                        groups[group].push(column);
                      });

                      return groups;
                    })()}
                    onApply={columns => {
                      setColumns(columns, clientId, fpId);
                      this.setState({ isColumnsPopoverOpen: false });
                    }}
                  />
                </li>
              </ul>
            </form>
          </>
        ) : null}

        {tableColumns && originalReport && (
          <section className={isPrint ? '' : 'widgetize'}>
            {tableColumns && (
              <div
                {...getTableClassProperties(
                  originalReport && originalReport.SpouseAges,
                  isPrint,
                  'advanced-reports-table-year',
                )}
                {...getTableStyleProperties(isPrint, numberOfRows)}
              >
                {this.state.yearHeaderState.className !== 'header' && (
                  <div className="placeholder-gap" />
                )}
                <ul {...this.state.yearHeaderState} ref={this.setYearHeaderRef}>
                  <li>
                    <span>Year</span>
                  </li>
                  <li className={originalReport.SpouseAges ? 'has-spouse' : ''}>
                    <span>
                      Age
                      {originalReport.SpouseAges ? 's' : ''}
                    </span>
                  </li>
                </ul>
                <ul className="data">
                  {originalReport.Periods.map(({ Text, IsToggleable, IsMonth }) => (
                    <li key={Text}>
                      <span>
                        {IsToggleable && (
                          <RawButton
                            css="margin-right: 4px; display: inline-block"
                            onClick={() => {
                              const expandCurrentYear = !IsMonth;

                              fetchSelectedReport(clientId, fpId, null, expandCurrentYear);
                            }}
                            aria-label="View Current Year"
                          >
                            <Icon
                              className={IsMonth ? 'far fa-minus-circle' : 'far fa-plus-circle'}
                            />
                          </RawButton>
                        )}
                        {Text}
                      </span>
                    </li>
                  ))}
                </ul>
                <ul className="data data-age">
                  {originalReport.PrincipalAges.map((principalAge, i) => (
                    <li key={i}>
                      <span>{principalAge}</span>
                    </li>
                  ))}
                </ul>
                {originalReport.SpouseAges && (
                  <ul className="data data-age border">
                    {originalReport.SpouseAges.map((spouseAge, i) => (
                      <li key={i}>
                        <span>{spouseAge}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div
              {...getTableClassProperties(
                originalReport && originalReport.SpouseAges,
                isPrint,
                `
                            adv-scrollbars
                            advanced-reports-table
                            columns-${numberOfColumns}
                        `,
              )}
              {...getTableStyleProperties(isPrint, numberOfRows)}
              onScroll={this.updateDataHeaderStatePropertiesIfNecessary}
            >
              {this.state.dataHeaderState.className !== 'header' ? (
                <div className="placeholder-gap" />
              ) : null}
              {originalReport && (
                <DragSortableList
                  tag="ul"
                  className={this.state.dataHeaderState.className}
                  style={
                    this.state.dataHeaderState.hasOwnProperty('style')
                      ? {
                          width: this.state.dataHeaderState.style.width,
                        }
                      : {}
                  }
                  ref={this.setDataHeaderRef}
                  keepRelativePosition
                  items={columnsHeader}
                  onSort={this.onSort}
                  dropBackTransitionDuration={0.2}
                  type="horizontal"
                />
              )}
              <ul>{columnsTable || null}</ul>
            </div>
          </section>
        )}

        {!tableColumns ? (
          <div className="empty">
            <i className="fa fa-columns" aria-hidden="true" />
            <h2>You have not selected any columns.</h2>
          </div>
        ) : null}
      </div>
    );
  }
}

export default AdvancedReportsView;
